import { type Ref, ref, watch, computed, toRaw, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNotifications } from '@common';
import { createForm, SelectableFormElement, type FormValidatorSettings } from '@common';
import { useCropStore } from '@shared';
import { type ParcelGroupItem } from '../models/parcel-group.model';
import { type OfferItem } from '../models/offer-item.model';
import { type ParcelGroupModalConfig } from '../models/parcel-group-modal.model';
import { type ParcelItem } from '../models/parcel-item.model';
import { getFilterFormConfig, parcelGroupModalFormConfig } from '../config/parcel-group-modal-form.config';

export const useParcelGroupModal = (config: ParcelGroupModalConfig) => {
  const offers: Ref<OfferItem[]> = ref(config.offers);
  const parcelGroups: Ref<ParcelGroupItem[]> = ref(config.parcelGroups);

  const selectedParcels: Ref<ParcelItem[]> = ref([]);
  const selectedGroup: Ref<OfferItem | ParcelGroupItem | undefined> = ref(undefined);

  const notificationService = useNotifications();
  const { t } = useI18n();
  const cropStore = useCropStore();

  const groupForm = createForm(parcelGroupModalFormConfig);
  const filterForm = createForm(getFilterFormConfig(config.actualCropName));

  const filteredOffers = computed(() => {
    return filter(offers.value);
  });

  const filteredParcelGroups = computed(() => {
    return filter(parcelGroups.value);
  });

  const isHideGroupForm = computed(() => config.hideGroupForm === true);

  const filter = (groups: OfferItem[] | ParcelGroupItem[]) => {
    if (filterForm.value.value.parcelName || filterForm.value.value.currentCrop) {
      return groups
        .map((group) => {
          return {
            ...group,
            parcels: group.parcels.filter((parcel) => {
              if (filterForm.value.value.parcelName && !filterForm.value.value.currentCrop) {
                return parcel.name.toLowerCase().includes(filterForm.value.value.parcelName.toLowerCase());
              }
              if (!filterForm.value.value.parcelName && filterForm.value.value.currentCrop) {
                return parcel.currentCrop === filterForm.value.value.currentCrop;
              } else {
                return (
                  parcel.name.toLowerCase().includes(filterForm.value.value.parcelName.toLowerCase()) &&
                  parcel.currentCrop === filterForm.value.value.currentCrop
                );
              }
            })
          };
        })
        .filter((group) => group.parcels.length);
    }
    return groups.filter((group) => group.parcels.length);
  };

  const onConfirm = async (close: () => void) => {
    if (!config.hideGroupForm) {
      const validationResult = await groupForm.validate();

      if (!validationResult.valid) return;
    }

    if (!selectedParcels.value.length) {
      notificationService.showError(t('parcel.validation.missingParcelsFromGroup'));
    } else {
      config.onConfirm?.call(this, selectedParcels.value, groupForm.value.value.name);
      close();
    }
  };

  const onCancel = (close: () => void) => {
    config.onCancel?.call(this);
    close();
  };

  watch(
    () => filteredOffers.value,
    () => (selectedGroup.value = undefined)
  );

  const selectGroup = (group: OfferItem) => {
    selectedGroup.value = group;
  };

  const selectParcel = (parcel: ParcelItem) => {
    if (
      !selectedParcels.value.includes(parcel) &&
      selectedParcels.value.length < (config.maxSelectableParcels ?? Infinity) &&
      !parcel.blocked &&
      !parcel.archived &&
      (!config.blockWithoutValidZoning || (config.blockWithoutValidZoning && parcel.hasValidZoning)) &&
      (!config.inclineLayersBlocked || (config.inclineLayersBlocked && !parcel.hasInclineLayers))
    ) {
      selectedParcels.value.push(parcel);
    }
  };

  const unselectParcel = (parcel: ParcelItem) => {
    const index = selectedParcels.value.indexOf(parcel, 0);
    if (index > -1) {
      selectedParcels.value.splice(index, 1);
    }
  };

  const unselectAll = () => {
    selectedParcels.value = [];
  };

  const selectAll = () => {
    if (selectedGroup.value) {
      selectedGroup.value.parcels.forEach((parcel) => {
        selectParcel(parcel);
      });
    }
  };

  const loadCrops = async () => {
    await cropStore.loadCrops().catch();

    const currentCropField = filterForm.getChild('currentCrop') as SelectableFormElement;

    if (currentCropField) {
      const parcelCrops: string[] = [];
      offers.value.forEach((offer) => {
        offer.parcels.forEach((parcel) => {
          if (parcel.currentCrop && !parcelCrops.includes(parcel.currentCrop)) {
            parcelCrops.push(parcel.currentCrop);
          }
        });
      });

      parcelGroups.value.forEach((group) => {
        group.parcels.forEach((parcel) => {
          if (parcel.currentCrop && !parcelCrops.includes(parcel.currentCrop)) {
            parcelCrops.push(parcel.currentCrop);
          }
        });
      });

      const filteredCrops = cropStore.crops.filter((crop) => parcelCrops.includes(crop.code));

      currentCropField.items = toRaw(filteredCrops);
    }
  };

  const validateUniqueGroupName = () => {
    const groupNames = parcelGroups.value.map((parcelGroup) => parcelGroup.name);

    if (config.selectedGroup) {
      const i = groupNames.indexOf(config.selectedGroup.name, 0);
      if (i > -1) {
        groupNames.splice(i, 1);
      }
    }

    const nameField = groupForm.getChild('name');

    if (nameField) {
      nameField.addValidator({
        validator: 'uniqueInStringList',
        settings: {
          invalidMessage: 'parcel.dialog.newParcelGroup.notUniqueGroupName',
          needTranslate: true,
          stringList: groupNames
        } as FormValidatorSettings
      });
    }
  };

  const setSelectedData = () => {
    setSelectedParcels();
    setGroupName();
  };

  const setSelectedParcels = () => {
    if (config.selectedGroup) {
      config.selectedGroup.parcels.forEach((group) => selectedParcels.value.push(group));
    }
  };

  const setGroupName = () => {
    if (config.selectedGroup) {
      groupForm.value.value.name = config.selectedGroup.name;
    }
  };

  const setSelectedParcelIds = () => {
    if (!config.selectedParcelIds) {
      return;
    }
    offers.value.forEach((offer) => {
      offer.parcels.forEach((parcel) => {
        if (
          config.selectedParcelIds?.includes(parcel.id) &&
          !selectedParcels.value.map((p) => p.id).includes(parcel.id)
        ) {
          selectedParcels.value.push(parcel);
        }
      });
    });
    parcelGroups.value.forEach((group) => {
      group.parcels.forEach((parcel) => {
        if (
          config.selectedParcelIds?.includes(parcel.id) &&
          !selectedParcels.value.map((p) => p.id).includes(parcel.id)
        ) {
          selectedParcels.value.push(parcel);
        }
      });
    });
  };

  onMounted(() => {
    loadCrops();
    validateUniqueGroupName();

    if (config.selectedGroup) {
      setSelectedData();
    }
    setSelectedParcelIds();
  });

  return {
    selectGroup,
    selectParcel,
    unselectParcel,
    selectedParcels,
    selectAll,
    unselectAll,
    filteredOffers,
    selectedGroup,
    filteredParcelGroups,
    onConfirm,
    onCancel,
    groupForm,
    filterForm,
    isHideGroupForm
  };
};
